import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, ChevronRight, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { resolveImageUrl } from "../lib/imageUrl";
import { useRefreshOnResume } from "../hooks/useRefreshOnResume";
import { useTablet } from "../hooks/useTablet";
import { useSeo } from "../lib/seo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Catalog() {
  const { tablet, cols } = useTablet();
  const navigate = useNavigate();
  const { t, tField } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const { categorySlug } = useParams();

  // tabs: 'categories' | 'brands'
  const [tab, setTab] = useState(searchParams.get("tab") === "brands" ? "brands" : "categories");
  const [products, setProducts] = useState([]);
  const [tree, setTree] = useState([]); // categories tree from API
  const [brands, setBrands] = useState([]);
  const [category, setCategory] = useState(categorySlug || "all"); // category slug or 'all'
  const [subcategory, setSubcategory] = useState(""); // subcategory slug
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const brandFilter = searchParams.get("brand") || "";

  const load = useCallback(async () => {
    try {
      const [p, c, b] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/brands`),
      ]);
      setProducts(p.data || []);
      setTree(Array.isArray(c.data) ? c.data : []);
      setBrands(Array.isArray(b.data) ? b.data : []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Категория из адреса страницы (/app/category/:slug) — SEO-посадочная
  useEffect(() => { if (categorySlug) setCategory(categorySlug); }, [categorySlug]);

  // Возврат в приложение из фона — подтягиваем новинки, цены и остатки.
  useRefreshOnResume(load);



  const currentTop = useMemo(() => tree.find(t => t.slug === category), [tree, category]);
  const subcats = currentTop?.subcategories || [];

  useSeo(categorySlug && currentTop ? {
    title: `${currentTop.name} — ANGIOLINI, Алматы`,
    description: `${currentTop.name} в бутике ANGIOLINI в Алматы: актуальные модели каталога с ценами в тенге.`,
    canonical: `/app/category/${categorySlug}`,
  } : {
    title: "Каталог — итальянская верхняя одежда в Алматы | ANGIOLINI",
    description: "Каталог ANGIOLINI: женские пуховики, кашемировые пальто, модели с мехом и шубы итальянских марок. Цены в тенге, примерка в бутике в Алматы.",
    canonical: "/app/catalog",
  });

  const filtered = useMemo(() => {
    let res = products;
    if (category !== "all" && currentTop) {
      const topSlug = currentTop.slug;
      const topName = currentTop.name;
      const subs = currentTop.subcategories || [];
      res = res.filter(p => {
        if (subcategory) {
          const sub = subs.find(s => s.slug === subcategory);
          if (!sub) return false;
          // Prefer slug-based match; fall back to name-based for legacy rows
          if (p.subcategory_slug === sub.slug) return true;
          return p.subcategory === sub.name || p.category === sub.name;
        }
        // Prefer slug-based match; fall back to name-based for legacy rows
        if (p.category_slug === topSlug) return true;
        if (subs.some(s => p.subcategory_slug === s.slug)) return true;
        return p.category === topName || subs.some(s => p.subcategory === s.name);
      });
    }
    if (brandFilter) res = res.filter(p => p.brand === brandFilter);
    if (search) res = res.filter(p => (tField(p, "name") || p.name || "")?.toLowerCase().includes(search.toLowerCase()));
    return res;
  }, [category, subcategory, brandFilter, search, products, currentTop]);

  const clearBrand = () => {
    const sp = new URLSearchParams(searchParams);
    sp.delete("brand");
    setSearchParams(sp, { replace: true });
  };

  return (
    <div style={{ background: "var(--ang-bg)", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ padding: "52px 20px 12px" }}>
        <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "30px", color: "var(--ang-text)", fontWeight: 400, marginBottom: "14px" }}>{categorySlug && currentTop ? currentTop.name : t.catalog.title}</h1>

        {/* Tabs */}
        <div data-testid="catalog-tabs" style={{ display: "flex", gap: "20px", marginBottom: "14px", borderBottom: "1px solid var(--ang-border)" }}>
          {[
            { id: "categories", label: "Категории" },
            { id: "brands", label: "Бренды" },
          ].map(({ id, label }) => (
            <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
              style={{ background: "none", border: "none", color: tab === id ? "var(--ang-gold)" : "var(--ang-text-muted)", fontFamily: "Manrope", fontSize: "13px", fontWeight: tab === id ? 700 : 500, letterSpacing: "0.08em", textTransform: "uppercase", padding: "10px 0", cursor: "pointer", position: "relative" }}>
              {label}
              {tab === id && <span style={{ position: "absolute", left: 0, right: 0, bottom: "-1px", height: "2px", background: "var(--ang-gold)" }} />}
            </button>
          ))}
        </div>

        <div style={{ position: "relative" }}>
          <Search size={14} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "var(--ang-text-faint)" }} />
          <input data-testid="catalog-search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t.catalog.search}
            style={{ width: "100%", background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "4px", padding: "12px 14px 12px 38px", color: "var(--ang-text)", fontFamily: "Manrope", fontSize: "14px", outline: "none" }} />
        </div>
      </div>

      {tab === "brands" ? (
        <BrandsList brands={brands} navigate={navigate} />
      ) : (
        <>
          {/* Category Filter */}
          <div style={{ padding: "0 0 12px", overflowX: "auto" }}>
            <div style={{ display: "flex", gap: "8px", padding: "0 20px", width: "max-content" }}>
              <button data-testid="cat-all" onClick={() => { setCategory("all"); setSubcategory(""); if (categorySlug) navigate("/app/catalog", { replace: true }); }}
                style={chipStyle("all" === category)}>
                {t.catalog.all}
              </button>
              {tree.map(c => (
                <button key={c.id} data-testid={`cat-${c.slug}`} onClick={() => { setCategory(c.slug); setSubcategory(""); if (categorySlug) navigate(`/app/category/${c.slug}`, { replace: true }); }}
                  style={chipStyle(c.slug === category)}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* Subcategory Filter */}
          {subcats.length > 0 && (
            <div style={{ padding: "0 0 12px", overflowX: "auto" }}>
              <div style={{ display: "flex", gap: "6px", padding: "0 20px", width: "max-content" }}>
                <button data-testid="subcat-all" onClick={() => setSubcategory("")}
                  style={subChipStyle("" === subcategory)}>
                  Все
                </button>
                {subcats.map(s => (
                  <button key={s.id} data-testid={`subcat-${s.slug}`} onClick={() => setSubcategory(s.slug)}
                    style={subChipStyle(s.slug === subcategory)}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Brand filter chip */}
          {brandFilter && (
            <div style={{ padding: "0 20px 12px" }}>
              <span data-testid="brand-filter-chip" style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "var(--ang-gold-soft)", border: "1px solid var(--ang-gold-line)", color: "var(--ang-gold)", borderRadius: "20px", padding: "5px 6px 5px 12px", fontFamily: "Manrope", fontSize: "12px" }}>
                Бренд: {brandFilter}
                <button onClick={clearBrand} aria-label="Сбросить бренд"
                  style={{ background: "transparent", border: "none", color: "var(--ang-gold)", cursor: "pointer", padding: "2px", display: "flex" }}>
                  <X size={13} />
                </button>
              </span>
            </div>
          )}

          {/* Products Grid */}
          <div style={{ padding: "0 16px 24px" }}>
            {loading ? (
              <div data-testid="products-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: tablet ? "18px" : "12px" }}>
                {[1,2,3,4].map(i => <div key={i} style={{ background: "var(--ang-surface)", borderRadius: "6px", height: "240px", animation: "pulse 2s infinite" }} />)}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--ang-text-faint)", fontFamily: "Manrope", fontSize: "14px" }}>{t.catalog.notFound}</div>
            ) : (
              <div data-testid="products-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: tablet ? "18px" : "12px" }}>
                {filtered.map((p, i) => (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                    onClick={() => navigate(`/app/catalog/${p.id}`)} data-testid={`product-${p.id}`}
                    style={{ background: "var(--ang-surface)", borderRadius: "6px", overflow: "hidden", cursor: "pointer", border: "1px solid var(--ang-border)" }}>
                    <div style={{ position: "relative", aspectRatio: "9/16", background: "var(--ang-surface-2)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {p.images?.[0] ? (
                        <img src={resolveImageUrl(p.images[0], { w: 600 })} alt={`${tField(p, "name")}${p.brand ? " — " + String(p.brand).trim() : ""}`} loading="lazy"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={e => { e.target.style.display = "none"; e.target.parentElement.querySelector(".no-img-fallback")?.style.setProperty("display", "flex"); }} />
                      ) : null}
                      {!p.images?.[0] && (
                        <div className="no-img-fallback" style={{
                          width: "100%", height: "100%",
                          background: "linear-gradient(135deg, var(--ang-surface), var(--ang-surface-2))",
                          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                          gap: "8px", color: "var(--ang-gold-strong)",
                          fontFamily: "Cormorant Garamond", fontSize: "20px", padding: "20px", textAlign: "center"
                        }}>
                          <div style={{ fontFamily: "Italiana", fontSize: "28px", color: "var(--ang-gold-strong)" }}>A</div>
                          <div style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase" }}>Angiolini</div>
                        </div>
                      )}
                      {p.discount && (
                        <div data-testid={`discount-badge-${p.id}`} style={{ position: "absolute", top: "10px", right: "10px", background: "#E11D2A", color: "var(--ang-text)", fontFamily: "Manrope", fontSize: "13px", fontWeight: 900, padding: "5px 10px", borderRadius: "3px", letterSpacing: "0.02em", boxShadow: "0 2px 8px rgba(225,29,42,0.45)", border: "1px solid var(--ang-border-strong)" }}>-{p.discount}%</div>
                      )}
                      {p.is_new && !p.discount && (
                        <div style={{ position: "absolute", top: "8px", left: "8px", background: "rgba(10,10,10,0.8)", border: "1px solid var(--ang-gold-strong)", color: "var(--ang-gold)", fontFamily: "Manrope", fontSize: "9px", fontWeight: 700, padding: "2px 7px", borderRadius: "2px", letterSpacing: "0.1em" }}>NEW</div>
                      )}
                    </div>
                    <div style={{ padding: "10px" }}>
                      <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "var(--ang-gold-strong)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>{p.category}</div>
                      <div style={{ fontFamily: "Cormorant Garamond", fontSize: "14px", color: "var(--ang-text)", lineHeight: 1.3, marginBottom: "8px" }}>{tField(p, "name")}</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: "6px", flexWrap: "wrap" }}>
                        {p.old_price && <span style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", textDecoration: "line-through" }}>{(p.old_price).toLocaleString("ru-RU")} ₸</span>}
                        <span style={{ fontFamily: "Manrope", fontSize: "14px", color: p.old_price ? "#E11D2A" : "var(--ang-gold)", fontWeight: 700 }}>{(p.price).toLocaleString("ru-RU")} ₸</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function chipStyle(active) {
  return {
    padding: "8px 16px",
    borderRadius: "20px",
    border: `1px solid ${active ? "var(--ang-gold)" : "var(--ang-border)"}`,
    background: active ? "var(--ang-gold-soft)" : "transparent",
    color: active ? "var(--ang-gold)" : "var(--ang-text-muted)",
    fontFamily: "Manrope",
    fontSize: "12px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontWeight: active ? 600 : 400,
    transition: "all 0.2s",
  };
}

function subChipStyle(active) {
  return {
    padding: "6px 12px",
    borderRadius: "14px",
    border: `1px solid ${active ? "var(--ang-gold-line)" : "var(--ang-border)"}`,
    background: active ? "var(--ang-gold-soft)" : "transparent",
    color: active ? "var(--ang-gold)" : "var(--ang-text-faint)",
    fontFamily: "Manrope",
    fontSize: "11px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontWeight: active ? 600 : 400,
    transition: "all 0.2s",
  };
}

function BrandsList({ brands, navigate }) {
  const { cols } = useTablet();
  const brandCols = Math.max(1, cols - 1);
  const { tField } = useLanguage();
  if (!brands || brands.length === 0) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--ang-text-faint)", fontFamily: "Manrope", fontSize: "14px" }}>
        Бренды скоро появятся
      </div>
    );
  }
  return (
    <div data-testid="brands-grid" style={{ padding: "4px 16px 24px", display: "grid", gridTemplateColumns: `repeat(${brandCols}, 1fr)`, gap: "12px" }}>
      {brands.map((b, i) => (
        <motion.div key={b.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
          onClick={() => navigate(`/app/brand/${b.slug}`)} data-testid={`brand-${b.slug}`}
          style={{ background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "8px", overflow: "hidden", cursor: "pointer", display: "flex" }}>
          <div style={{ width: "120px", height: "150px", flexShrink: 0, background: "var(--ang-surface-2)" }}>
            {b.cover_image ? (
              <img src={resolveImageUrl(b.cover_image, { w: 600 })} alt={tField(b, "name")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ang-gold-strong)", fontFamily: "Italiana", fontSize: "26px" }}>
                {tField(b, "name")?.[0] || "A"}
              </div>
            )}
          </div>
          <div style={{ flex: 1, padding: "14px 14px 14px 16px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", lineHeight: 1.1, marginBottom: "4px", fontWeight: 500 }}>
                {tField(b, "name")}
              </div>
              <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-gold-strong)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>
                {b.country || ""}{b.founded_year ? ` · с ${b.founded_year}` : ""}
              </div>
              {(tField(b, "description") || b.short_description) && (
                <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text-muted)", lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {tField(b, "description") || b.short_description}
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
              <span style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-text-faint)" }}>
                {b.products_count || 0} товаров
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--ang-gold)", fontFamily: "Manrope", fontSize: "11px", fontWeight: 600 }}>
                Открыть <ChevronRight size={13} />
              </span>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
